"use strict";
/*
 * Integration App React package
 * {@link https://www.npmjs.com/package/@integration-app/react}
 * v2.22.1
 */
'use client';

import { jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import { clsx } from 'clsx';
import '../../../styled-system/css/css.mjs';
import '../../../styled-system/helpers.mjs';
import { sva } from '../../../styled-system/css/sva.mjs';

const fieldControlPanelRecipe = sva({
  className: "field-control-panel",
  slots: ["root", "delimiter", "group", "button"],
  base: {
    root: {
      maxW: "full",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      flexDirection: "row",
      flexWrap: "wrap",
      columnGap: "2",
      rowGap: "1"
    },
    delimiter: {
      color: "slate.10",
      fontWeight: "medium"
    },
    group: {
      display: "flex",
      flexDir: "row",
      flexWrap: "nowrap",
      overflow: "hidden",
      maxW: "full"
    },
    button: {
      maxW: "full",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "2",
      transition: "all",
      transitionDuration: "200ms",
      bg: "slate.2",
      color: "slate.10",
      borderWidth: "1px",
      borderColor: "slate.3",
      rounded: "token(sizes.2.5)",
      fontWeight: "medium",
      m: "0",
      py: "1",
      px: "2.5",
      _hover: {
        bg: "slate.3",
        borderColor: "slate.4",
        color: "slate.11"
      },
      _disabled: {
        color: "slate.8",
        cursor: "not-allowed"
      },
      "& svg": {
        h: "3",
        w: "3",
        flexShrink: "0",
        flexGrow: "0",
        strokeWidth: "token(sizes.0.5)"
      },
      ".field-control-panel__group &": {
        rounded: "none",
        "&:not(:first-child)": {
          borderLeft: "none"
        },
        _first: {
          roundedLeft: "token(sizes.2.5)"
        },
        _last: {
          roundedRight: "token(sizes.2.5)"
        }
      }
    }
  },
  variants: {
    noWrap: {
      true: {
        root: {
          flexWrap: "nowrap"
        }
      },
      false: {}
    }
  }
});
function FieldControlPanelRoot({ children, noWrap }) {
  const styles = fieldControlPanelRecipe({ noWrap });
  return /* @__PURE__ */ jsx("div", { className: styles.root, children });
}
function FieldControlPanelDelimiter({ children }) {
  const styles = fieldControlPanelRecipe();
  return /* @__PURE__ */ jsx("span", { className: styles.delimiter, children });
}
function FieldControlButtonGroup({ children }) {
  const styles = fieldControlPanelRecipe();
  return /* @__PURE__ */ jsx("div", { className: styles.group, children });
}
const FieldControlButtonElement = forwardRef(({ children, className, ...props }, ref) => {
  const styles = fieldControlPanelRecipe();
  return /* @__PURE__ */ jsx("button", { ref, className: clsx(styles.button, className), ...props, children });
});
const FieldControlPanel = Object.assign(FieldControlPanelRoot, {
  Delimiter: FieldControlPanelDelimiter,
  ButtonGroup: FieldControlButtonGroup,
  Button: FieldControlButtonElement
});

export { FieldControlPanel, fieldControlPanelRecipe };
